import {
  type OptionMap,
  type VariantGroup,
  type VariationOption,
  type Option,
  type IProduct,
  type InternalVariant,
  productDataTransformer,
} from '@commerce-product-webcomponents/sdk';
import {
  PlanResponse,
  Product,
  ProductData,
  SubscriptionProductEntry,
  SubscriptionResponse,
  SubscriptionResponseStatusEnum,
  nullishOperator,
} from '@commerce-subscription-webcomponents/sdk';
import { shouldShowEditOrder } from './utils';

const getProductId = (id: string) => {
  const productIdArr = id.split('-');
  return productIdArr.length > 1 ? productIdArr[1] : productIdArr[0];
};

const getFirstItem = (option: OptionMap) => option?.product?.items?.[0];

const getCurrentVariant = (itemNumber: string, variantGroupData?: VariantGroup): OptionMap | undefined =>
  variantGroupData?.optionMap?.find((om) => getFirstItem(om)?.itemNumber === itemNumber);

const subscriptionProductIds = (entries: SubscriptionProductEntry[]) =>
  entries.map(({ product }) => getProductId(nullishOperator(product?.id, '')));

const getVariantIds = (optionMap: OptionMap[]) =>
  optionMap.map(({ product }) => getProductId(nullishOperator(product?.productId, '')));

const getVariantOptions = (optionMap: OptionMap[], variationOption: VariationOption[]): VariationOption[] => {
  const options = optionMap.map((element: OptionMap) => element.optionChoice);
  const displayOptions = variationOption[0]?.options?.filter((elements: Option) => options.includes(elements.value));
  const variationOptions: VariationOption[] = [
    {
      ...variationOption[0],
      options: displayOptions,
    },
  ];
  return variationOptions.slice();
};

const filterProductVariants = (products: ProductData[]) => {
  const productsByVariantGroup: IProduct[] = [];
  products.forEach((product) => {
    if (product) {
      const transformedData = { ...productDataTransformer(product) };
      productsByVariantGroup.push(transformedData);
    }
  });
  return productsByVariantGroup.slice();
};

const getProductIdsFromPlan = (plan: PlanResponse | undefined) => {
  const products: Array<Product> = [],
    prductIds = new Set<string>();
  if (plan?.productOptions && plan.productOptions.length > 0) {
    plan.productOptions.forEach((product) => {
      if (product) {
        products.push(product);
        if (product.productId) {
          prductIds.add(product.productId);
        }
      }
    });
  }
  return { ids: Array.from(prductIds), productPlan: products };
};

const groupByVariant = (
  productId: string,
  productPrice: number,
  variantData: VariantGroup,
  productIds: string[],
  isCategory: boolean,
  isVariantPriceChangeAllowed: boolean,
): VariantGroup => {
  const availableVariantsInPlan: VariantGroup = {
    optionMap: [],
    variationOptions: [],
    variationLevels: variantData.variationLevels,
  };
  variantData.optionMap?.forEach((option) => {
    const pId = nullishOperator(option.product?.productId, '');
    const productData = productDataTransformer(option.product as InternalVariant as ProductData);
    if (isVariantPriceChangeAllowed || productPrice === productData.productPrice) {
      if (productIds.includes(pId) || isCategory) {
        availableVariantsInPlan.optionMap?.push(option);
        if (pId === productId) {
          availableVariantsInPlan.defaultVariant = productId;
        }
      }
    }
  });

  availableVariantsInPlan.variationOptions = getVariantOptions(
    nullishOperator(availableVariantsInPlan.optionMap, []),
    nullishOperator(variantData.variationOptions, []),
  );

  return availableVariantsInPlan;
};

const getProductIdsByEntries = (subscriptionPlan: SubscriptionResponse['plan']) => {
  let isCategory = false;
  const productEntryIds: string[] =
    subscriptionPlan?.productOptions?.map((entry) => {
      isCategory = !!entry.isCategory;
      return nullishOperator(entry.productId, '');
    }) ?? [];
  return { productEntryIds, isCategory };
};

interface ProductVariantMapType {
  [key: string]: number;
}

const groupProductByAvailableVariants = (
  subscriptionData: SubscriptionResponse,
): { productGroupByVariant: IProduct[]; productVariantMap: ProductVariantMapType } => {
  const { productEntryIds, isCategory } = getProductIdsByEntries(subscriptionData?.plan);
  const productVariantMap: ProductVariantMapType = {};
  const productsByVariantGroup =
    subscriptionData?.products?.map((product) => {
      let transformedData = { ...productDataTransformer(product) };
      if (
        subscriptionData?.status !== SubscriptionResponseStatusEnum.Active ||
        !shouldShowEditOrder(subscriptionData)
      ) {
        transformedData = { ...transformedData, variantData: undefined };
      } else if (transformedData.variantData?.optionMap) {
        const variantData = {
          ...groupByVariant(
            product.id,
            transformedData.productPrice,
            transformedData.variantData,
            productEntryIds,
            isCategory,
            subscriptionData?.plan?.restrictions?.isVariantPriceChangeAllowed ?? false,
          ),
        };
        productVariantMap[transformedData?.productId] = variantData.optionMap?.length || 0;
        transformedData.variantData =
          variantData.optionMap && variantData.optionMap?.length > 1 ? variantData : undefined;
      }
      return transformedData;
    }) ?? [];
  return { productGroupByVariant: productsByVariantGroup.slice(), productVariantMap };
};

export {
  filterProductVariants,
  getProductId,
  getVariantIds,
  getCurrentVariant,
  getProductIdsFromPlan,
  subscriptionProductIds,
  groupProductByAvailableVariants,
};
